import { useCallback } from "react";
import { useSearch } from "wouter";
import { useShellUrlWriter } from "@/components/navigation/shellUrl.ts";

const LIGHTBOX_PARAM = "lightbox";

/**
 * Surface that opened the lightbox. Keyed alongside the artifact id so the
 * omnibox biscuit and the inline chip for the *same* artifact don't both open
 * off one URL param.
 */
type LightboxSurface = "biscuit" | "inline";

/**
 * URL-backed open state for an artifact lightbox (`?lightbox=<surface>:<id>`).
 *
 * The lightbox used to be local `useState`, which is lost whenever the page
 * unmounts — notably on mobile, where opening a linked email navigates to a new
 * full-screen route, so Back returned to the thread with the lightbox gone.
 * Driving it from the URL means Back (and refresh) restore the lightbox.
 *
 * Opening pushes a real history entry so the browser/OS Back button closes the
 * lightbox (and Back from a linked email returns to it). Closing pops that entry
 * (`history.back`) rather than replacing it — replacing would leave a duplicate
 * of the page behind, making the header Back button take two presses. The pop
 * keeps the browser Back and the header Back button in sync. Falls back to
 * replace-remove when there's no in-app entry to pop (deep link / refresh).
 */
export const useLightboxUrlState = (
  surface: LightboxSurface,
  artifactId: string,
): readonly [boolean, (next: boolean) => void] =>
  useUrlOpenState(LIGHTBOX_PARAM, `${surface}:${artifactId}`);

/**
 * The open state behind `useLightboxUrlState`, for an overlay that needs its
 * own query param: `?<param>=<token>` means open. Another param lets an overlay
 * open on top of an artifact lightbox without replacing its token, which would
 * close the lightbox underneath.
 */
export const useUrlOpenState = (
  param: string,
  token: string,
): readonly [boolean, (next: boolean) => void] => {
  const search = useSearch();
  const writeShellUrl = useShellUrlWriter();
  const isOpen = new URLSearchParams(search).get(param) === token;

  const setOpen = useCallback(
    (next: boolean) => {
      if (next) {
        // Push — a real Back-able entry.
        writeShellUrl({ params: { [param]: token } });
        return;
      }
      // Only act when the open overlay is ours — never clobber another's.
      if (!isOpen) return;
      // Pop the entry we pushed on open (`_appDepth`, set by useBackNavigation,
      // confirms there's an in-app entry to pop). Without one — deep link or
      // refresh straight onto the param — drop it via replace instead.
      if ((window.history.state?._appDepth ?? 0) > 0) {
        window.history.back();
        return;
      }
      writeShellUrl({ params: { [param]: null } }, { replace: true });
    },
    [isOpen, param, token, writeShellUrl],
  );

  return [isOpen, setOpen] as const;
};
